/**
 * Badge component module.
 * @module shared/components/Badge
 */
import React, {useEffect} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import moment from 'moment';
import 'moment-timezone';
import {
  _getPendingCompliance,
  _getUnreadNotifications,
  _getUnreadReports,
  _setCompliancePageVisitTimestamp,
  _setNotificationModalOpenTimestamp,
  _setPendingComplianceTimeoutID,
  _setReportPageVisitTimestamp,
  _setUnreadNotificationsTimeoutID,
  _setUnreadReportsTimeoutID,
  isAuthorized,
  isEntitled,
} from '../store';

const _BadgeWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1;
`;

// TODO need reqs for positioning of badge over icons/labels - currently inconsistent from user to user in Header
const _Badge = styled.div`
  position: absolute;
  top: ${props => props.top || ''};
  bottom: ${props => props.bottom || ''};
  left: ${props => props.left || ''};
  right: ${props => props.right || ''};
  width: ${props => props.diameter || '10px'};
  height: ${props => props.diameter || '10px'};
  background-color: yellow;
  border: 1px solid #000;
  border-radius: 50%;
`;

/**
 * Badge component to display a badge over a label or icon.
 * @name Badge
 */
const Badge = (props) => {
  const {
    types, position, lastResize, IsSubsectionBadge, session, unreadNotificationsTimeoutID, unreadReportsTimeoutID,
    pendingComplianceTimeoutID, unreadNotifications, unreadReports, pendingCompliance, notificationModalOpenTimestamp,
    reportPageVisitTimestamp, compliancePageVisitTimestamp, getUnreadNotifications, getUnreadReports, getPendingCompliance,
    setUnreadNotificationsTimeoutID, setUnreadReportsTimeoutID, setPendingComplianceTimeoutID,
    setNotificationModalOpenTimestamp, setReportPageVisitTimestamp, setCompliancePageVisitTimestamp,
  } = props;

  const pollUnreadReportsIfEntitled = () => {
    if (isEntitled([{group: 'reports', feature: 'core', access: 'c'}, {group: 'reports', feature: 'core', access: 'r'}])
      && !isAuthorized(['ASSOC', 'PRODSUPP', 'PRODMGR'])) {
      if ((window.location.pathname === '/report') && moment().subtract(500, 'ms').isAfter(lastResize)) {
        setReportPageVisitTimestamp(moment().tz('America/New_York').format('YYYY-MM-DD HH:mm:ss'));
      } else {
        getUnreadReports(unreadReportsTimeoutID, unreadReports, reportPageVisitTimestamp, 300000);
      }
    }
  };

  const pollPendingComplianceIfEntitled = () => {
    if (isEntitled([
      {group: 'advisor', feature: 'client_engagement', access: 'r'},
      {group: 'compliance', feature: 'core', access: 'r'},
    ], true) && isAuthorized(['CPLO', 'FIRMMGR'])) {
      if ((window.location.pathname === '/compliance/approval') && moment().subtract(500, 'ms').isAfter(lastResize)) {
        setCompliancePageVisitTimestamp(moment().tz('America/New_York').format('YYYY-MM-DD HH:mm:ss'));
      } else {
        getPendingCompliance(pendingComplianceTimeoutID, pendingCompliance, compliancePageVisitTimestamp);
      }
    }
  };

  const clearBadgeState = () => {
    setUnreadNotificationsTimeoutID(null);
    setUnreadReportsTimeoutID(null);
    setPendingComplianceTimeoutID(null);

    setNotificationModalOpenTimestamp(null);
    setReportPageVisitTimestamp(null);
    setCompliancePageVisitTimestamp(null);
  };

  useEffect(() => {
    if (!localStorage.getItem('token') || IsSubsectionBadge) {return;}

    getUnreadNotifications(unreadNotificationsTimeoutID, unreadNotifications, notificationModalOpenTimestamp);
    pollUnreadReportsIfEntitled();
    pollPendingComplianceIfEntitled();

    return () => {
      if (moment().subtract(500, 'ms').isBefore(lastResize) || IsSubsectionBadge) {return;}

      if (unreadNotificationsTimeoutID.value !== null) {
        clearTimeout(unreadNotificationsTimeoutID.value);
      }

      if (unreadReportsTimeoutID.value !== null) {
        clearTimeout(unreadReportsTimeoutID.value);
      }

      if (pendingComplianceTimeoutID.value !== null) {
        clearTimeout(pendingComplianceTimeoutID.value);
      }

      clearBadgeState();
    };
  }, []);

  useEffect(() => {
    if (!localStorage.getItem('token') || IsSubsectionBadge) {return;}

    pollUnreadReportsIfEntitled();
    pollPendingComplianceIfEntitled();

    return () => {
      if (unreadNotificationsTimeoutID.value !== null) {
        clearTimeout(unreadNotificationsTimeoutID.value);
      }

      if (unreadReportsTimeoutID.value !== null) {
        clearTimeout(unreadReportsTimeoutID.value);
      }

      if (pendingComplianceTimeoutID.value !== null) {
        clearTimeout(pendingComplianceTimeoutID.value);
      }
    };
  }, [session.runAs]);

  useEffect(() => {
    if (!localStorage.getItem('token')) {return;}

    getUnreadNotifications(unreadNotificationsTimeoutID, unreadNotifications, notificationModalOpenTimestamp);
  }, [notificationModalOpenTimestamp.value]);

  useEffect(() => {
    if (!localStorage.getItem('token')) {return;}

    if (isEntitled([
      {group: 'advisor', feature: 'client_engagement', access: 'r'},
      {group: 'compliance', feature: 'core', access: 'r'},
    ], true) && isAuthorized(['CPLO', 'FIRMMGR'])) {
      getPendingCompliance(pendingComplianceTimeoutID, pendingCompliance, compliancePageVisitTimestamp);
    }
  }, [compliancePageVisitTimestamp.value]);

  if (!localStorage.getItem('token')) {
    clearTimeout(unreadNotificationsTimeoutID.value);
    clearTimeout(unreadReportsTimeoutID.value);
    clearTimeout(pendingComplianceTimeoutID.value);
  }

  const badge = (

    <_BadgeWrapper>
      <_Badge
        top={position.top}
        bottom={position.bottom}
        left={position.left}
        right={position.right}
      />
    </_BadgeWrapper>
  );

  let toggledBadge = null;

  if (((types.includes('notification')) && unreadNotifications.value)
    || ((types.includes('report')) && unreadReports.value)
    || ((types.includes('compliance')) && pendingCompliance.value)) {
    toggledBadge = badge;
  }

  return toggledBadge;
};

const mapStateToProps = state => ({
  session: state.session,
  unreadNotificationsTimeoutID: state.badges.unreadNotificationsTimeoutID,
  unreadReportsTimeoutID: state.badges.unreadReportsTimeoutID,
  pendingComplianceTimeoutID: state.badges.pendingComplianceTimeoutID,
  unreadNotifications: state.badges.unreadNotifications,
  unreadReports: state.badges.unreadReports,
  pendingCompliance: state.badges.pendingCompliance,
  notificationModalOpenTimestamp: state.badges.notificationModalOpenTimestamp,
  reportPageVisitTimestamp: state.badges.reportPageVisitTimestamp,
  compliancePageVisitTimestamp: state.badges.compliancePageVisitTimestamp,
  rerenderToggle: state.badges.rerenderToggle,
});

const mapDispatchToProps = dispatch => ({
  setUnreadNotificationsTimeoutID: timeout => dispatch(_setUnreadNotificationsTimeoutID(timeout)),
  setUnreadReportsTimeoutID: timeout => dispatch(_setUnreadReportsTimeoutID(timeout)),
  setPendingComplianceTimeoutID: timeout => dispatch(_setPendingComplianceTimeoutID(timeout)),
  setNotificationModalOpenTimestamp: timestamp => dispatch(_setNotificationModalOpenTimestamp(timestamp)),
  setReportPageVisitTimestamp: timestamp => dispatch(_setReportPageVisitTimestamp(timestamp)),
  setCompliancePageVisitTimestamp: timestamp => dispatch(_setCompliancePageVisitTimestamp(timestamp)),
  getUnreadNotifications: (timeoutID, unread, timestamp) => dispatch(_getUnreadNotifications(timeoutID, unread, timestamp)),
  getUnreadReports: (timeoutID, unread, timestamp, interval) => dispatch(_getUnreadReports(timeoutID, unread, timestamp, interval)),
  getPendingCompliance: (timeoutID, unread, timestamp) => dispatch(_getPendingCompliance(timeoutID, unread, timestamp)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Badge);

Badge.propTypes = {
  /**
   * The diameter of the badge
   */
  diameter: PropTypes.string,
  /**
   * The labels or icons the badge is for ('notification', 'report', and/or 'compliance')
   */
  types: PropTypes.arrayOf(PropTypes.string).isRequired,
  /**
   * Absolute position of badge within parent
   */
  position: PropTypes.shape({
    top: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    bottom: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    left: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    right: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /**
   * Last window resize (moment)
   */
  lastResize: PropTypes.object.isRequired,
  /**
   * Boolean - true for subsection badge (in menu), false for main header badge
   */
  IsSubsectionBadge: PropTypes.bool.isRequired,
  /**
   * Is from user reducer
   */
  session: PropTypes.shape().isRequired,
  /**
   * Are from badges reducer
   */
  unreadNotificationsTimeoutID: PropTypes.object,
  unreadReportsTimeoutID: PropTypes.object,
  pendingComplianceTimeoutID: PropTypes.object,
  unreadNotifications: PropTypes.object,
  unreadReports: PropTypes.object,
  pendingCompliance: PropTypes.object,
  notificationModalOpenTimestamp: PropTypes.object,
  reportPageVisitTimestamp: PropTypes.object,
  compliancePageVisitTimestamp: PropTypes.object,
  getUnreadNotifications: PropTypes.func,
  getUnreadReports: PropTypes.func,
  getPendingCompliance: PropTypes.func,
  setUnreadNotificationsTimeoutID: PropTypes.func,
  setUnreadReportsTimeoutID: PropTypes.func,
  setPendingComplianceTimeoutID: PropTypes.func,
  setNotificationModalOpenTimestamp: PropTypes.func,
  setReportPageVisitTimestamp: PropTypes.func,
  setCompliancePageVisitTimestamp: PropTypes.func,
  rerenderToggle: PropTypes.bool,
};
